/**
 * Detección de "sesión muerta en el servidor".
 *
 * El access token de Supabase se valida por firma, así que PostgREST lo acepta
 * hasta que expira (~1 h) aunque la fila de `auth.sessions` ya no exista: logout
 * en otra pestaña, revocación del refresh token o cierre de sesión desde otro
 * dispositivo. Las Edge Functions que llaman `auth.getUser()` sí consultan esa
 * fila y responden 401, de modo que media aplicación sigue cargando datos y la
 * otra media truena con "Edge Function returned a non-2xx status code" — el
 * usuario ve un error de página, no un aviso de sesión.
 *
 * Estos helpers no importan el cliente de Supabase a propósito: quedan puros y
 * testeables sin los `define` de Vite.
 */

/** Códigos de GoTrue que significan "esta sesión ya no existe en el servidor". */
const CODIGOS_SESION_MUERTA = new Set([
  "session_not_found",
  "session_expired",
  "refresh_token_not_found",
  "refresh_token_already_used",
  "user_not_found",
  "user_banned",
]);

type ErrorAuthParcial = {
  status?: number;
  code?: string;
  message?: string;
  name?: string;
};

/**
 * True solo si el error de `supabase.auth.getUser()` indica que la sesión murió
 * del lado del servidor. Un fallo de red (`AuthRetryableFetchError`, status 0 o
 * 5xx) devuelve false: cerrar sesión por perder el wifi un segundo sería peor
 * que el bug original.
 */
export function esSesionMuertaEnServidor(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as ErrorAuthParcial;

  if (err.name === "AuthRetryableFetchError") return false;
  if (err.code && CODIGOS_SESION_MUERTA.has(err.code)) return true;
  if (err.status === 401 || err.status === 403) return true;

  return false;
}

/**
 * True si una `FunctionsHttpError` de `supabase.functions.invoke` viene con 401:
 * las Edge Functions del proyecto responden así cuando falta el header de
 * Authorization o cuando `auth.getUser()` no reconoce la sesión.
 */
export function esSesionInvalidaEdgeFunction(error: unknown): boolean {
  const ctx = (error as { context?: { status?: number } } | null)?.context;
  return ctx?.status === 401;
}

/**
 * True si el error ya trae un status de acceso (401 sesión / 403 permisos).
 * Reintentar esos casos solo repite la misma respuesta y esconde el motivo.
 */
export function esErrorDeAcceso(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status === 401 || status === 403;
}

/** True si el error corresponde a una sesión que el servidor ya no reconoce. */
export function esErrorDeSesion(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 401;
}

/** Aviso único para el usuario cuando su sesión dejó de ser válida. */
export const MENSAJE_SESION_INVALIDA =
  "Tu sesión ya no es válida en el servidor (se cerró en otra pestaña o venció). Vuelve a iniciar sesión.";
